/** A thread, changelog entry or article that teammates can have open together. */
export type DocPresenceKey =
	| `thread:${string}`
	| `changelog:${string}`
	| `article:${string}`

export interface DocViewer {
	userId: string
	name: string
	imageUrl?: string | null
}

/**
 * A spot in the doc as top-level block plus offset inside it, so a peer whose copy
 * is a few keystrokes behind still draws the caret in the right paragraph.
 */
export interface DocPoint {
	block: number
	offset: number
	/** The block's opening text, to find it in a copy whose block numbers lag behind. */
	text?: string
	/** How many blocks the sender's copy has; a match means block numbers line up. */
	blocks?: number
	/** The opening text of the block above, so a short or empty line is not matched to a lookalike. */
	before?: string
	/** Which line inside the block (a line break or a list item starts one), and how far into it. */
	line?: number
	lineOffset?: number
}

/** How much of a block's opening text a point carries. */
export const DOC_POINT_TEXT_LENGTH = 32

/** A selection; `anchor` equal to `head` is a plain caret. */
export interface DocCursor {
	anchor: DocPoint
	head: DocPoint
}

export interface DocPresenceState {
	typing?: boolean
	cursor?: DocCursor | null
}

export interface DocJoinData extends DocViewer {
	workspaceId: string
	docKey: DocPresenceKey
}

export interface DocLeaveData {
	docKey: DocPresenceKey
}

export interface DocStateData extends DocPresenceState {
	docKey: DocPresenceKey
}

/** What a socket in the room hears about one of its peers. */
export interface DocPeerData extends DocPresenceState {
	docKey: DocPresenceKey
	socketId: string
	viewer: DocViewer
	/** Set when the peer just arrived, so everyone already there announces back. */
	joined?: boolean
}

export interface DocPeerLeftData {
	docKey: DocPresenceKey
	socketId: string
}

/** A typing flag older than this is dropped, so a lost "stopped" never sticks. */
export const DOC_TYPING_TTL_MS = 6_000

/** Typing is re-announced at most this often while the person keeps typing. */
export const DOC_TYPING_THROTTLE_MS = 2_000

/** Caret moves are sent at most this often; still reads as live, at half the traffic of 80. */
export const DOC_CURSOR_THROTTLE_MS = 160

const DOC_KEY = /^(thread|changelog|article):[\w:-]{1,200}$/

export function isDocPresenceKey(value: unknown): value is DocPresenceKey {
	return typeof value === "string" && DOC_KEY.test(value)
}

const isCount = (value: unknown): value is number =>
	Number.isInteger(value) && (value as number) >= 0

function parsePoint(value: unknown): DocPoint | null {
	const point = value as
		| { block?: unknown; offset?: unknown }
		| null
		| undefined
	if (!point || !isCount(point.block) || !isCount(point.offset)) return null
	const { text, blocks, before, line, lineOffset } = point as {
		text?: unknown
		blocks?: unknown
		before?: unknown
		line?: unknown
		lineOffset?: unknown
	}
	return {
		block: point.block,
		offset: point.offset,
		...(typeof text === "string"
			? { text: text.slice(0, DOC_POINT_TEXT_LENGTH) }
			: {}),
		...(isCount(blocks) ? { blocks } : {}),
		...(typeof before === "string"
			? { before: before.slice(0, DOC_POINT_TEXT_LENGTH) }
			: {}),
		...(isCount(line) && isCount(lineOffset) ? { line, lineOffset } : {}),
	}
}

/** The selection a peer sent, or null when either end is not a block and offset. */
export function parseDocCursor(value: unknown): DocCursor | null {
	const cursor = value as
		| { anchor?: unknown; head?: unknown }
		| null
		| undefined
	const anchor = parsePoint(cursor?.anchor)
	const head = parsePoint(cursor?.head)
	return anchor && head ? { anchor, head } : null
}
